'use client'

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { User } from 'firebase/auth'
import { usePathname } from 'next/navigation'
import { auth } from '../../../lib/firebase'
import { createPost, addReply, subscribeToPostsBySymbol, Post } from '../../../lib/firestore'
import { PostCard } from './PostCard'
import { CreatePostForm } from './CreatePostForm'
import { SymbolSelector } from './SymbolSelector'
import styles from './Posts.module.css'

/**
 * @uiComponent
 * @name Posts
 * @description The Posts component provides a social trading platform for sharing market insights and discussions.
 * It features Firebase-powered real-time posts, symbol-specific discussions, user authentication integration,
 * and interactive commenting system. Users can create posts, reply to discussions, and follow stock-specific conversations.
 * @location Posts plugin in the trading terminal
 * @prop none - No external props, manages internal state for posts, authentication, and symbol selection
 */

/**
 * @uiWorkflow
 * @name Create and Share Trading Posts
 * @description How to share market insights and trading ideas with the community
 * @platform posts
 * @step 1 Navigate to the Posts plugin [component: Posts] [action: navigate]
 * @step 2 Ensure you are logged in [component: Posts] [action: authenticate] [notes: Firebase authentication required]
 * @step 3 Select a stock symbol to discuss [component: SymbolSelector] [action: choose] [notes: Posts are symbol-specific]
 * @step 4 Wait for symbol data to load [component: Posts] [action: wait] [notes: LTP and symbol info fetched]
 * @step 5 Click on the create post form [component: CreatePostForm] [action: focus]
 * @step 6 Write your trading insight or analysis [component: CreatePostForm] [action: type] [notes: Share market views, strategies]
 * @step 7 Submit your post to the community [component: CreatePostForm] [action: submit]
 * @step 8 View your post in the feed [component: Posts] [action: observe] [notes: Real-time updates via Firebase]
 * @step 9 Monitor replies and engagement [component: PostCard] [action: track]
 * @step 10 Respond to comments on your posts [component: Posts] [action: engage]
 */

/**
 * @uiWorkflow
 * @name Follow Symbol-Specific Discussions
 * @description How to participate in conversations about specific stocks
 * @platform posts
 * @step 1 Access the Posts social platform [component: Posts] [action: navigate]
 * @step 2 Use the symbol selector to choose a stock [component: SymbolSelector] [action: select] [notes: Filter posts by symbol]
 * @step 3 Browse existing posts for the symbol [component: Posts] [action: scroll] [notes: Real-time feed updates]
 * @step 4 Read community insights and analysis [component: PostCard] [action: read]
 * @step 5 Click reply on interesting posts [component: PostCard] [action: interact]
 * @step 6 Add your perspective or questions [component: Posts] [action: reply] [notes: Requires authentication]
 * @step 7 Follow the conversation thread [component: Posts] [action: monitor]
 * @step 8 Switch symbols to explore other discussions [component: SymbolSelector] [action: change]
 * @step 9 Discover new trading opportunities [component: Posts] [action: research]
 */

/**
 * @uiWorkflow
 * @name Manage Authentication and User Profile
 * @description How to handle login and user-specific features
 * @platform posts
 * @step 1 Check authentication status [component: Posts] [action: verify] [notes: Firebase auth state monitoring]
 * @step 2 Log in if not authenticated [component: Posts] [action: authenticate] [notes: Required for posting and replies]
 * @step 3 Verify user display name [component: Posts] [action: check] [notes: Shows as author on posts]
 * @step 4 Create posts with authenticated user [component: Posts] [action: post] [notes: User ID and name attached]
 * @step 5 Reply to posts as authenticated user [component: Posts] [action: reply]
 * @step 6 Monitor your posts and replies [component: Posts] [action: track] [notes: Real-time updates]
 * @step 7 Handle authentication errors gracefully [component: Posts] [action: error-handle]
 * @step 8 Log out when session ends [component: Posts] [action: logout] [notes: Auth state automatically managed]
 */

const Posts = () => {
  const [user, setUser] = useState<User | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState<string>('')
  const [selectedLtp, setSelectedLtp] = useState<number>(0)
  const pathname = usePathname()

  // Extract symbol from URL path
  useEffect(() => {
    const pathSegments = (pathname ?? '').split('/').filter(Boolean)
    const symbolFromUrl = pathSegments[0] || 'BTCUSDT' // Default symbol
    setSelectedSymbol(symbolFromUrl.toUpperCase())
  }, [pathname])

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser)
    })
    return () => unsubscribe()
  }, [])

  // Subscribe to posts for selected symbol
  useEffect(() => {
    if (!selectedSymbol) {
      setPosts([])
      setLoading(false)
      return
    }

    setLoading(true)
    const unsubscribe = subscribeToPostsBySymbol(selectedSymbol, (newPosts: Post[]) => {
      setPosts(newPosts)
      setLoading(false)
    })
    
    return () => unsubscribe()
  }, [selectedSymbol])

  // Handle symbol change from selector
  const handleSymbolChange = useCallback((symbol: string, ltp: number) => {
    setSelectedSymbol(symbol)
    setSelectedLtp(ltp)
  }, [])

  // Handle creating a new post
  const handleCreatePost = useCallback(async (content: string) => {
    if (!user) {
      setError('You must be logged in to post')
      return
    }

    if (!selectedSymbol || selectedLtp <= 0) {
      setError('Please select a valid symbol before posting')
      return
    }

    try {
      setError(null)
      await createPost(content, user.displayName || 'Anonymous', user.uid, selectedSymbol, selectedLtp)
    } catch (err) {
      setError('Failed to create post')
      console.error('Error creating post:', err)
    }
  }, [user, selectedSymbol, selectedLtp])

  // Handle adding a reply
  const handleAddReply = useCallback(async (postId: string, content: string) => {
    if (!user) {
      setError('You must be logged in to reply')
      return
    }

    if (!selectedSymbol) {
      setError('Symbol not available for reply')
      return
    }

    try {
      setError(null)
      await addReply(selectedSymbol, postId, content, user.displayName || 'Anonymous', user.uid)
    } catch (err) {
      setError('Failed to add reply')
      console.error('Error adding reply:', err)
    }
  }, [user, selectedSymbol])

  // Memoized posts list to prevent unnecessary re-renders
  const postsList = useMemo(() => (
    posts.map((post) => (
      <PostCard
        key={post.id}
        post={post}
        currentUser={user}
        onReply={handleAddReply}
      />
    ))
  ), [posts, user, handleAddReply])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading posts...</div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Posts - {selectedSymbol || 'Select Symbol'}</h2>
        {user ? (
          <span className={styles.userInfo}>
            Welcome, {user.displayName || 'User'}!
          </span>
        ) : (
          <span className={styles.authPrompt}>
            Sign in to create posts and replies
          </span>
        )}
      </div>

      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      {/* Symbol selector */}
      <SymbolSelector
        initialSymbol={selectedSymbol}
        onSymbolChange={handleSymbolChange}
        disabled={loading}
      />

      {/* Create post form - only show if user is logged in and symbol is selected */}
      {user && selectedSymbol && selectedLtp > 0 && (
        <CreatePostForm 
          onSubmit={handleCreatePost}
          selectedSymbol={selectedSymbol}
          selectedLtp={selectedLtp}
        />
      )}

      {/* Posts feed */}
      <div className={styles.feed}>
        {!selectedSymbol ? (
          <div className={styles.emptyState}>
            Please select a symbol to view posts.
          </div>
        ) : posts.length === 0 ? (
          <div className={styles.emptyState}>
            No posts yet for {selectedSymbol}. {user && selectedLtp > 0 ? 'Be the first to post!' : 'Sign in to start the conversation!'}
          </div>
        ) : (
          postsList
        )}
      </div>
    </div>
  )
}

export default Posts